import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, Grid, IconButton, Pagination, Stack, TextField, Typography } from '@mui/material';
import config from 'src/config';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import showLoading from 'src/common/loading';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { getContainers } from 'src/services/container.service';
import ContainerCardComponent from '../../components/containers/ContainerCard.component';
import AddEditContainerModal from './AddEdit.modal';

// ----------------------------------------------------------------------

const containerTypeMap = {
    'container': {
        name: 'Group',
        name_plural: 'Groups'
    },
    'department': {
        name: 'Department',
        name_plural: 'Departments'
    },
    'team': {
        name: 'Team',
        name_plural: 'Teams'
    },
    'project': {
        name: 'Project',
        name_plural: 'Projects'
    }
}


class ContainersList extends React.Component {

    constructor(props) {
        super(props);

        this.state = {

            role: localStorage.getItem('role') || config.USER_ROLES.USER,

            showAddEditContainerModal: false,
            containers: [],
            type: this.props?.payload?.type || 'container',
            search: '',
            limit: 12,
            skip: 0,
            total_containers: 0,
        }
    }

    getMapValue = (key) => {
        return containerTypeMap[this.state.type][key] || '';
    }

    // promised setState
    setStateAsync(state) {
        return new Promise((resolve) => {
            this.setState(state, resolve)
        });
    }

    async componentDidMount() {
        await this.fetchContainers();
    }

    fetchContainers = async(showLoader = true) => {
        try {

            if (showLoader)
                showLoading(true);


            const options = {
                limit: this.state.limit,
                skip: this.state.skip,
                sort_key: 'updated_at',
                sort_order: -1,
                type: this.state.type
            }

            if (this.state.search)
                options.search = this.state.search;

            const containers = await getContainers(options);
            if (containers.error) {

                showLoading(false);

                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: containers.error,
                    confirmButtonText: 'Ok',
                    confirmButtonColor: config.primaryColor
                });
                return;
            }

            this.setState({
                containers: containers.containers || [],
                total_containers: containers.count || 0
            });
            showLoading(false);

        } catch (error) {
            showLoading(false);
            console.error("Error fetching:", error.message)
        }
    }


    handleSearch = async (e) => {

        await this.setStateAsync({
            skip: 0,
            search: e.target.value
        })

        await this.fetchContainers(false);
    }

    render() {
        return (
            <>
                <Helmet>
                    <title> {this.getMapValue('name_plural')} | {config.APP_NAME} </title>
                </Helmet>

                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                        <Typography variant="h4" style={{ display: 'flex', alignItems: 'center' }}>
                            {this.getMapValue('name_plural')}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center" width={'50%'} flex={1} paddingLeft={10} paddingRight={10}>
                            <TextField
                                size='small'
                                label={'Search'}
                                variant="outlined"
                                fullWidth
                                value={this.state.search}
                                InputProps={{ endAdornment: <SearchIcon /> }}
                                onChange={this.handleSearch}
                            />
                            <IconButton aria-label="clear" size="large"
                                onClick={async () => {
                                    await this.setStateAsync({
                                        skip: 0,
                                        search: ''
                                    })
                                    await this.fetchContainers();
                                }}
                            >
                                <ClearIcon />
                            </IconButton>
                        </Stack>
                        <Button
                            variant='contained'
                            color='primary'
                            size='small'
                            onClick={() => {
                                this.setState({
                                    showAddEditContainerModal: true,
                                });
                            }}
                        >
                            Create {this.getMapValue('name')} 
                        </Button>
                    </Grid>
                </Grid>

                <Grid container spacing={2}>

                    {(this.state.containers.map((container) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={container._id}>
                            <ContainerCardComponent
                                container={container}
                                type={this.state.type}
                                fetchContainers={this.fetchContainers}
                            />
                        </Grid>
                    )))}
                </Grid>

                <Stack spacing={2} sx={{ alignItems: 'center' }}>
                    <Pagination
                        page={(this.state.skip / this.state.limit) + 1}
                        count={Math.ceil(this.state.total_containers / this.state.limit)}
                        shape="rounded"
                        size="large"
                        onChange={async (event, page) => {
                            await this.setState({
                                skip: (page - 1) * this.state.limit
                            });
                            await this.fetchContainers();
                        }}
                        showFirstButton={true}
                        showLastButton={true}
                        boundaryCount={2}
                        style={{ margin: 20 }}
                    />
                </Stack>


                {(this.state.showAddEditContainerModal) ? (
                    <AddEditContainerModal
                        open={this.state.showAddEditContainerModal}
                        onClose={() => {
                            this.setState({
                                showAddEditContainerModal: false
                            });
                        }}
                        container={this.state.selectedContainer}
                        payload={{
                            type: this.state.type
                        }}
                        onSave={async () => {
                            await this.setStateAsync({
                                showAddEditContainerModal: false
                            });
                            await this.fetchContainers();
                        }}
                    />
                ) : null}

            </>
        );
    }
}

export default function (props) {
    const navigate = useNavigate();

    return <ContainersList {...props} navigate={navigate} />;
}

const styles={
	fileNameStyles:{
		flex: 1,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	}
}